<template>
  <aside :class="asideClasses">
    <!-- Brand -->
    <div class="px-4 pt-4 pb-3 flex items-center gap-2.5">
      <LeLogo :size="28" />
      <span
        class="font-bold text-lg tracking-tight bg-gradient-to-br from-brand-700 to-brand-500 bg-clip-text text-transparent flex-1 truncate"
      >
        LogElite
      </span>
    </div>

    <!-- Établissement compact -->
    <div v-if="authStore.currentEstablishment" class="px-3 mb-2">
      <button
        type="button"
        :class="estClasses"
        :title="
          authStore.hasMultipleEstablishments
            ? 'Changer d\'établissement'
            : authStore.currentEstablishment.name
        "
        @click="authStore.hasMultipleEstablishments && $router.push('/select-establishment')"
      >
        <span
          class="w-7 h-7 rounded-lg bg-gradient-to-br from-brand-500 to-accent-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
        >
          {{ (authStore.currentEstablishment.name || '?').charAt(0).toUpperCase() }}
        </span>
        <span class="flex-1 min-w-0 text-left">
          <span
            class="block text-[10px] uppercase tracking-wider text-ink-400 font-semibold leading-tight"
            >Centre</span
          >
          <span class="block text-xs font-semibold text-ink-900 truncate">{{
            authStore.currentEstablishment.name
          }}</span>
        </span>
        <svg
          v-if="authStore.hasMultipleEstablishments"
          class="w-3.5 h-3.5 text-ink-400 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M5 9l7-7 7 7M5 15l7 7 7-7" />
        </svg>
      </button>
    </div>

    <!-- Search global -->
    <div class="px-3 mb-2">
      <button
        type="button"
        class="w-full flex items-center gap-2 px-3 py-2 rounded-xl bg-ink-50 hover:bg-ink-100 active:bg-ink-200 transition-colors text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-100"
        @click="openSearchPalette"
      >
        <svg
          class="w-4 h-4 text-ink-400 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M21 21l-4-4m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <span class="text-xs text-ink-500 flex-1 truncate">Aller à…</span>
        <kbd
          class="text-[10px] font-mono text-ink-400 bg-white border border-ink-200 rounded px-1.5 py-0.5 flex-shrink-0"
          >⌘K</kbd
        >
      </button>
    </div>

    <!-- Menu principal — flex-1 + scroll robuste -->
    <nav
      class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden px-3 pb-2"
      aria-label="Navigation principale"
    >
      <div v-for="(group, gi) in groupedItems" :key="group.label || `g-${gi}`">
        <p
          v-if="group.label"
          class="text-[10px] uppercase tracking-wider text-ink-400 font-semibold px-3 mt-3 mb-1"
        >
          {{ group.label }}
        </p>
        <ul class="space-y-0.5">
          <li v-for="item in group.items" :key="item.label">
            <button type="button" :class="navItemClasses(item)" @click="navigate(item.route)">
              <span
                v-if="isActive(item)"
                class="absolute left-0 top-1.5 bottom-1.5 w-0.5 bg-brand-500 rounded-r"
                aria-hidden="true"
              />
              <span class="flex-shrink-0 [&_svg]:w-4 [&_svg]:h-4" v-html="item.icon" />
              <span class="flex-1 text-left truncate">{{ item.label }}</span>
              <LeBadge v-if="item.badge" :variant="item.badgeVariant || 'accent'" size="sm">
                {{ item.badge }}
              </LeBadge>
            </button>
          </li>
        </ul>
      </div>
    </nav>

    <!-- Profil + logout — toujours visible en bas -->
    <div class="flex-shrink-0 p-3 border-t border-ink-200 bg-white">
      <button
        type="button"
        class="w-full flex items-center gap-2.5 p-2 rounded-xl hover:bg-ink-100 transition-colors focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-100"
        @click="$router.push('/profile')"
      >
        <LeAvatar
          :firstname="authStore.currentUser?.firstname"
          :lastname="authStore.currentUser?.lastname"
          size="sm"
          status="online"
        />
        <div class="flex-1 text-left min-w-0">
          <p class="text-xs font-semibold text-ink-900 truncate">{{ userName }}</p>
          <p class="text-[10px] text-ink-500 truncate">{{ authStore.currentUser?.email }}</p>
        </div>
        <svg
          class="w-3.5 h-3.5 text-ink-400 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </button>
      <button
        type="button"
        class="w-full flex items-center justify-center gap-2 px-3 py-2 mt-1.5 rounded-xl text-red-600 hover:bg-red-50 transition-colors text-xs font-semibold focus:outline-none focus-visible:ring-4 focus-visible:ring-red-100"
        @click="handleLogout"
      >
        <svg
          class="w-4 h-4 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          viewBox="0 0 24 24"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"
          />
        </svg>
        Déconnexion
      </button>
    </div>

    <!-- Command palette -->
    <Teleport to="body">
      <Transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-150 ease-in"
        leave-to-class="opacity-0"
      >
        <div
          v-if="paletteOpen"
          class="fixed inset-0 z-[1100] bg-ink-900/40 backdrop-blur-sm flex items-start justify-center p-4 pt-20"
          @click.self="paletteOpen = false"
        >
          <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0 scale-95 -translate-y-2"
            enter-to-class="opacity-100 scale-100 translate-y-0"
            leave-active-class="transition duration-150 ease-in"
            leave-to-class="opacity-0 scale-95"
          >
            <div
              v-if="paletteOpen"
              class="w-full max-w-xl bg-white rounded-2xl shadow-xl2 border border-ink-200 overflow-hidden"
              role="dialog"
              aria-label="Rechercher dans l'app"
            >
              <div class="relative border-b border-ink-100">
                <svg
                  class="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M21 21l-4-4m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                <input
                  ref="paletteInputRef"
                  v-model="paletteQuery"
                  type="text"
                  placeholder="Aller à une page, rechercher…"
                  class="w-full pl-12 pr-4 py-4 bg-transparent text-base placeholder-ink-400 outline-none border-0"
                  @keydown.esc="paletteOpen = false"
                  @keydown.enter="onPaletteEnter"
                  @keydown.down.prevent="movePaletteFocus(1)"
                  @keydown.up.prevent="movePaletteFocus(-1)"
                />
                <kbd
                  class="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-mono text-ink-400 bg-ink-50 border border-ink-200 rounded px-1.5 py-0.5"
                  >Esc</kbd
                >
              </div>

              <div class="max-h-[60vh] overflow-y-auto py-2">
                <div
                  v-if="paletteResults.length === 0"
                  class="px-4 py-10 text-center text-sm text-ink-500"
                >
                  Aucun résultat pour « {{ paletteQuery }} »
                </div>
                <button
                  v-for="(res, idx) in paletteResults"
                  :key="res.route + idx"
                  type="button"
                  :class="[
                    'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors',
                    paletteFocus === idx
                      ? 'bg-brand-50 text-brand-700'
                      : 'text-ink-700 hover:bg-ink-50'
                  ]"
                  @click="navigateFromPalette(res)"
                  @mouseenter="paletteFocus = idx"
                >
                  <span
                    class="w-8 h-8 rounded-lg bg-ink-100 text-ink-600 flex items-center justify-center flex-shrink-0 [&_svg]:w-4 [&_svg]:h-4"
                    v-html="res.icon"
                  />
                  <span class="flex-1 min-w-0">
                    <span class="block text-sm font-semibold truncate">{{ res.label }}</span>
                    <span class="block text-xs text-ink-500 truncate">{{ res.route }}</span>
                  </span>
                  <svg
                    class="w-3.5 h-3.5 text-ink-400"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"
                    />
                  </svg>
                </button>
              </div>

              <div
                class="border-t border-ink-100 px-4 py-2 flex items-center justify-between text-2xs text-ink-400"
              >
                <span class="inline-flex items-center gap-2">
                  <kbd class="font-mono bg-ink-50 border border-ink-200 rounded px-1 py-0.5"
                    >↑↓</kbd
                  >
                  Naviguer
                  <kbd class="font-mono bg-ink-50 border border-ink-200 rounded px-1 py-0.5">↵</kbd>
                  Ouvrir
                </span>
                <span
                  >{{ paletteResults.length }} résultat{{
                    paletteResults.length > 1 ? 's' : ''
                  }}</span
                >
              </div>
            </div>
          </Transition>
        </div>
      </Transition>
    </Teleport>
  </aside>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { usePermissions } from '@/composables/usePermissions'
import LeLogo from '@/components/ui/LeLogo.vue'
import LeAvatar from '@/components/ui/LeAvatar.vue'
import LeBadge from '@/components/ui/LeBadge.vue'

const props = defineProps({
  mini: { type: Boolean, default: false }
})

defineEmits(['open-search'])

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { hasAnyPermission, hasAllPermissions, isSuperAdmin } = usePermissions()

const icons = {
  home: '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3 12l9-9 9 9M5 10v10h14V10"/></svg>',
  calendar:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>',
  desmos:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2M9 12h6m-6 4h6"/></svg>',
  heart:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z"/></svg>',
  stock:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M20 7l-8-4-8 4m16 0v10l-8 4-8-4V7m16 0l-8 4M4 7l8 4m0 0v10"/></svg>',
  orders:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 17v-6a2 2 0 012-2h2a2 2 0 012 2v6m-9 4h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>',
  lab: '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M14.25 3.104v5.714a2.25 2.25 0 00.659 1.591L19 14.5M9.75 3.104a48.967 48.967 0 015 0M5 14.5L3.75 19.5h16.5L19 14.5M5 14.5h14"/></svg>',
  leave:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3M3 11h18M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>',
  payslip:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5l5 5v11a2 2 0 01-2 2z"/></svg>',
  accounting:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3 6l3 12 6-3 6 3 3-12M7 3h10"/></svg>',
  users:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.4-1.8M17 20H7m10 0v-2c0-.7-.1-1.3-.4-1.8M7 20H2v-2a3 3 0 015.4-1.8M7 20v-2c0-.7.1-1.3.4-1.8m0 0a5 5 0 019.2 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>',
  profile:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><circle cx="12" cy="7" r="4"/><path stroke-linecap="round" stroke-linejoin="round" d="M6 21v-2a4 4 0 014-4h4a4 4 0 014 4v2"/></svg>',
  pointage:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z"/></svg>',
  establishment:
    '<svg fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-5h6v5M9 11h.01M15 11h.01"/></svg>'
}

const items = [
  { label: 'Accueil', route: '/', icon: icons.home, perms: [], any: false },
  {
    label: 'Calendrier',
    route: '/calendar',
    icon: icons.calendar,
    perms: ['read_own_schedule', 'read_schedules'],
    any: true
  },
  {
    label: 'Desmos',
    route: '/desmos',
    icon: icons.desmos,
    perms: ['write_patient_cares'],
    any: false
  },
  {
    label: 'Prise en charge',
    route: '/patient-care',
    icon: icons.heart,
    perms: ['read_patients', 'read_patient_cares', 'read_own_patient_cares'],
    any: true
  },
  {
    label: 'Stock',
    route: '/stock',
    icon: icons.stock,
    perms: ['write_products', 'delete_products'],
    any: true
  },
  {
    label: 'Laboratoire',
    route: '/lab-order',
    icon: icons.lab,
    perms: ['view_lab_page', 'create_lab_form'],
    any: true
  },
  { label: 'Pointage', route: '/pointage', icon: icons.pointage, perms: [], any: false },
  {
    label: 'Congés',
    route: '/leave-request',
    icon: icons.leave,
    perms: ['read_own_leave_requests', 'read_leave_requests'],
    any: true
  },
  {
    label: 'Fiches de paie',
    route: '/payslips',
    icon: icons.payslip,
    perms: ['read_payslips', 'read_own_payslips'],
    any: true
  }
]
const adminItems = [
  {
    label: 'Utilisateurs',
    route: '/user-management',
    icon: icons.users,
    perms: ['admin_access', 'read_users'],
    any: false
  },
  {
    label: 'Centres',
    route: '/establishments',
    icon: icons.establishment,
    superadminOnly: true
  }
]

const canSee = (item) => {
  // `superadminOnly` se vérifie sur le rôle : hasAnyPermission et
  // hasAllPermissions renvoient true pour tout rôle admin, qui verrait sinon
  // une entrée menant à un écran que l'API lui refuse.
  if (item.superadminOnly) return isSuperAdmin.value
  if (!item.perms?.length) return true
  return item.any ? hasAnyPermission(item.perms) : hasAllPermissions(item.perms)
}

const groupedItems = computed(() => {
  const main = items.filter(canSee)
  const admin = adminItems.filter(canSee)
  const groups = [{ label: '', items: main }]
  if (admin.length) groups.push({ label: 'Admin', items: admin })
  return groups
})

const userName = computed(() => {
  const u = authStore.currentUser
  if (!u) return 'Utilisateur'
  const fn = (u.firstname || '').trim()
  const ln = (u.lastname || '').trim()
  if (!fn && !ln) return u.email || 'Utilisateur'
  const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '')
  return `${cap(fn)} ${cap(ln)}`.trim()
})

const asideClasses = computed(() => [
  'fixed top-0 left-0 h-screen z-50 flex flex-col bg-white border-r border-ink-200 transition-all duration-200',
  props.mini ? 'w-16' : 'w-60'
])

const estClasses = computed(() => [
  'w-full flex items-center gap-2 px-3 py-2 rounded-xl transition-colors',
  authStore.hasMultipleEstablishments
    ? 'bg-ink-50 hover:bg-ink-100 cursor-pointer focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-100'
    : 'bg-ink-50 cursor-default'
])

const isActive = (item) => {
  if (item.route === '/') return route.path === '/'
  return route.path.startsWith(item.route)
}

const navItemClasses = (item) => [
  'relative w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
  'focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-100',
  isActive(item)
    ? 'bg-brand-50 text-brand-700 font-semibold'
    : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900'
]

const navigate = (path) => {
  if (route.path !== path) router.push(path)
}

const handleLogout = async () => {
  await authStore.logout()
  router.push('/login')
}

// ============ Command palette ============
const paletteOpen = ref(false)
const paletteQuery = ref('')
const paletteFocus = ref(0)
const paletteInputRef = ref(null)

const allPaletteItems = computed(() => {
  const all = [...items, ...adminItems].filter(canSee)
  return all.map((i) => ({ label: i.label, route: i.route, icon: i.icon }))
})

const paletteResults = computed(() => {
  const q = paletteQuery.value.trim().toLowerCase()
  if (!q) return allPaletteItems.value
  return allPaletteItems.value.filter(
    (i) => i.label.toLowerCase().includes(q) || i.route.toLowerCase().includes(q)
  )
})

const openSearchPalette = async () => {
  paletteOpen.value = true
  paletteQuery.value = ''
  paletteFocus.value = 0
  await nextTick()
  paletteInputRef.value?.focus()
}

const onPaletteEnter = () => {
  const res = paletteResults.value[paletteFocus.value]
  if (res) navigateFromPalette(res)
}
const navigateFromPalette = (res) => {
  paletteOpen.value = false
  if (route.path !== res.route) router.push(res.route)
}
const movePaletteFocus = (delta) => {
  const n = paletteResults.value.length
  if (!n) return
  paletteFocus.value = (paletteFocus.value + delta + n) % n
}

watch(paletteQuery, () => {
  paletteFocus.value = 0
})
watch(paletteOpen, (open) => {
  if (typeof document === 'undefined') return
  document.body.style.overflow = open ? 'hidden' : ''
})

// ⌘K / Ctrl+K
const onKeydown = (e) => {
  if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
    e.preventDefault()
    openSearchPalette()
  }
}
onMounted(() => window.addEventListener('keydown', onKeydown))
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
  document.body.style.overflow = ''
})
</script>
